/**
 * Layer 10: Security Policy Engine (SPE) — POLISHED
 * Armadillo Adamantium Security Framework
 * 
 * Polish additions:
 * - Live policy reload without redeployment
 * - Versioned policies with rollback
 * - Dry-run mode (evaluate without enforcing)
 * - Policy tests (verify rules before activation)
 */

import { SecurityContext, RoutePolicy, SecurityPolicy, SecurityDecision, TrustTierName, PolicyVersion } from './types';
import { verifyToken, hasScope } from './cte';

// ============================================
// DEFAULT SECURITY POLICY
// ============================================

export const DEFAULT_POLICY: SecurityPolicy = {
  routes: {
    '/api/bounties/*': {
      path: '/api/bounties/*',
      methods: ['GET', 'POST'],
      requiredActions: ['bounty:read', 'bounty:write'],
      minTrustTier: 'verified',
      rateLimit: {
        maxRequests: 100,
        windowSeconds: 3600
      }
    },
    '/api/auth/*': {
      path: '/api/auth/*',
      methods: ['POST'],
      requiredActions: ['auth:login'],
      minTrustTier: 'untrusted',
      rateLimit: {
        maxRequests: 20,
        windowSeconds: 3600
      }
    },
    '/api/admin/*': {
      path: '/api/admin/*',
      methods: ['GET', 'POST', 'PUT', 'DELETE'],
      requiredActions: ['admin:full'],
      minTrustTier: 'armadillo',
      rateLimit: {
        maxRequests: 50,
        windowSeconds: 3600
      }
    }
  },
  globalSettings: {
    defaultTrustTier: 'probationary',
    enableVRF: true,
    enableGodel: true,
    enablePQ: false // Enable when ML-KEM is available
  }
};

// ============================================
// POLICY MANAGEMENT
// ============================================

let currentPolicy: SecurityPolicy = { ...DEFAULT_POLICY };

/**
 * Load policy from cache
 */
export function loadCachedPolicy(): boolean {
  try {
    const cached = localStorage.getItem('armadillo_policy');
    if (cached) {
      currentPolicy = JSON.parse(cached);
      return true;
    }
  } catch {
    // Ignore cache errors
  }
  
  return false;
}

// ============================================
// POLICY ENFORCEMENT
// ============================================

/**
 * Evaluate security policy for a request
 * Returns security decision (allow/block/challenge)
 */
export async function evaluatePolicy(
  ctx: SecurityContext,
  policy?: SecurityPolicy
): Promise<SecurityDecision> {
  const effectivePolicy = policy || currentPolicy;
  
  // Find matching route policy
  const routePolicy = findRoutePolicy(ctx.path, ctx.method, effectivePolicy);
  
  if (!routePolicy) {
    // No policy found - use global default
    return {
      allowed: true,
      statusCode: 200,
      reason: 'No policy for this route'
    };
  }
  
  // Check trust tier requirement
  if (!meetsTrustTier(ctx.trustTier, routePolicy.minTrustTier)) {
    return {
      allowed: false,
      statusCode: 403,
      reason: `Insufficient trust tier. Required: ${routePolicy.minTrustTier}, Current: ${ctx.trustTier}`
    };
  }
  
  // Check required capability scope
  if (routePolicy.requiredActions.length > 0) {
    if (!ctx.capabilityToken) {
      return {
        allowed: false,
        statusCode: 401,
        reason: 'Capability token required'
      };
    }
    
    // Verify token
    const verification = await verifyToken(ctx.capabilityToken, ctx);
    if (!verification.valid) {
      return {
        allowed: false,
        statusCode: 401,
        reason: `Invalid token: ${verification.reason}`
      };
    }
    
    // Check if token has all required scopes
    for (const action of routePolicy.requiredActions) {
      if (!hasScope(verification.payload as any, action)) {
        return {
          allowed: false,
          statusCode: 403,
          reason: `Missing required scope: ${action}`
        };
      }
    }
  }
  
  // All checks passed
  return {
    allowed: true,
    statusCode: 200,
    reason: 'Policy satisfied'
  };
}

/**
 * Find matching route policy for request
 */
function findRoutePolicy(
  path: string,
  method: string,
  policy: SecurityPolicy
): RoutePolicy | undefined {
  for (const [routePath, routePolicy] of Object.entries(policy.routes)) {
    // Check if path matches (supports wildcards)
    if (pathMatches(path, routePath)) {
      // Check if method is allowed
      if (routePolicy.methods.includes(method.toUpperCase())) {
        return routePolicy;
      }
    }
  }
  
  return undefined;
}

/**
 * Check if request path matches route pattern
 * Supports wildcards: /api/*, /api/bounties/*
 */
function pathMatches(actualPath: string, pattern: string): boolean {
  if (pattern === actualPath) {
    return true;
  }

  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2); // Remove /*
    // Match the prefix itself or anything under it
    return actualPath === prefix || actualPath.startsWith(prefix + '/');
  }

  return false;
}

/**
 * Check if trust tier meets minimum requirement
 * Trust tiers ordered from lowest to highest:
 * untrusted < probationary < verified < trusted < elevated < armadillo
 */
function meetsTrustTier(
  current: TrustTierName,
  required: TrustTierName
): boolean {
  const tierOrder: TrustTierName[] = [
    'untrusted',
    'probationary',
    'verified',
    'trusted',
    'elevated',
    'armadillo'
  ];
  
  const currentIndex = tierOrder.indexOf(current);
  const requiredIndex = tierOrder.indexOf(required);
  
  return currentIndex >= requiredIndex;
}

/**
 * Get effective trust tier name from score
 */
export function trustTierFromScore(score: number): TrustTierName {
  if (score >= 86) return 'armadillo';
  if (score >= 71) return 'elevated';
  if (score >= 51) return 'trusted';
  if (score >= 31) return 'verified';
  if (score >= 11) return 'probationary';
  return 'untrusted';
}
